/**
 * Deadlines (MSPDI Deadline): a date a task should be finished by. A deadline
 * never moves the planning; it is only compared with the planned finish. Shared
 * by the server (project list, "Mijn planning", scheduler warning) and the
 * client (phase list, Gantt).
 *
 * TransectLibre stores a deadline like a finish: at the end of the working day
 * on the project calendar (17:00 at Transect). A deadline at 00:00 belongs to
 * the day before, like a finish at 24:00.
 */
import type { TaskMeta } from './model.js';
import { dayOf, MINUTES_PER_DAY, minuteOfDay, startOfDay, type DayIndex, type LocalMinutes } from './time.js';

export type DeadlineStatus = 'overschreden' | 'in-gevaar' | 'binnenkort';

/** "binnenkort": the deadline is at most this many days away. */
export const DEADLINE_SOON_DAYS = 14;
/** "Mijn planning" lists the deadlines up to this many days ahead. */
export const MY_DEADLINE_DAYS = 30;
/**
 * The overviews leave out overdue deadlines of projects that finished longer ago
 * than this (the planning goes back to 2018; such a phase is done but was never
 * ticked "afgerond"). Matches the views' default data window of about three months.
 */
export const OLD_PROJECT_DAYS = 90;

/** Most urgent first. */
export const DEADLINE_STATUS_RANK: Record<DeadlineStatus, number> = { overschreden: 0, 'in-gevaar': 1, binnenkort: 2 };

export const DEADLINE_STATUS_LABEL: Record<DeadlineStatus, string> = {
  overschreden: 'Deadline overschreden',
  'in-gevaar': 'Deadline in gevaar',
  binnenkort: 'Deadline binnenkort',
};

export interface DeadlineTask {
  deadline: LocalMinutes | null;
  finish: LocalMinutes | null;
  meta: Pick<TaskMeta, 'done'>;
}

/** Calendar day of a deadline (00:00 counts as the end of the day before). */
export function deadlineDay(deadline: LocalMinutes): DayIndex {
  return minuteOfDay(deadline) === 0 ? dayOf(deadline) - 1 : dayOf(deadline);
}

/** The deadline stored for a date: the end of that working day (`finishTime`, minutes after midnight). */
export function deadlineOnDay(day: DayIndex, finishTime: number): LocalMinutes {
  return startOfDay(day) + finishTime;
}

export function isDone(t: { meta: Pick<TaskMeta, 'done'> }): boolean {
  return !!t.meta.done;
}

/** The planned finish lies after the deadline. */
export function missesDeadline(t: Pick<DeadlineTask, 'deadline' | 'finish'>): boolean {
  return t.deadline !== null && t.finish !== null && t.finish > t.deadline;
}

/**
 * Status of a deadline that is not "afgerond":
 *  - overschreden: the deadline has passed;
 *  - in-gevaar: the deadline is still ahead, but the planned finish is after it;
 *  - binnenkort: on track and at most `soonDays` days away.
 * Null: no deadline, afgerond, or on track and further away.
 */
export function deadlineStatus(t: DeadlineTask, now: LocalMinutes, soonDays = DEADLINE_SOON_DAYS): DeadlineStatus | null {
  if (t.deadline === null || isDone(t)) return null;
  if (t.deadline < now) return 'overschreden';
  if (missesDeadline(t)) return 'in-gevaar';
  return deadlineDay(t.deadline) - dayOf(now) <= soonDays ? 'binnenkort' : null;
}

/** A project whose last phase finished more than OLD_PROJECT_DAYS ago (its overdue deadlines are not reported). */
export function isOldProject(projectFinish: LocalMinutes | null, now: LocalMinutes): boolean {
  return projectFinish !== null && projectFinish < now - OLD_PROJECT_DAYS * MINUTES_PER_DAY;
}

/** Orders deadlines most urgent first: by status (none last), then by date. */
export function compareDeadlines(a: { status: DeadlineStatus | null; deadline: LocalMinutes }, b: { status: DeadlineStatus | null; deadline: LocalMinutes }): number {
  const rank = (s: DeadlineStatus | null) => (s === null ? 3 : DEADLINE_STATUS_RANK[s]);
  return rank(a.status) - rank(b.status) || a.deadline - b.deadline;
}
